import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, history, useIntl, getLocale } from 'umi';
import StoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore';
import ExportTable from '@intelligence/components/ExportTable';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import MobileStoreRankTopFilter from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/AllTopChartsFilter';
import type { IAllTopChartsFilterInstance } from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/AllTopChartsFilter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useStoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore/_hooks/useStoreRankTopCore';
import useHotTagsOptions from '@/pages/intelligence/hooks/useHotTagsOptions';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useMobileStoreRankRegionOptions from '@intelligence/v3/_hooks/useMobileStoreRankRegionOptions';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import SvgIcon from '@home/components/SvgIcon';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
  GENRETOR as FILTER_GENRE_SELECT,
  RANGE_TIME as FILTER_RANGE_TIME,
  SINGLE_PLATFORM_SELECT as FILTER_SINGLE_PLATFORM_SELECT,
  SENSOR_TOWER_TAG_PRODUCT_MODEL,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  isSameDate,
  handleGenreFilterParams,
  FilterTimeType,
} from '@intelligence/utils/filterUtils';
import './index.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';
import {
  getFilterName,
  getFilterValueStorage,
} from '@/pages/intelligence/components/Filter/util';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';

import { MOBILE_SOURCES } from '@/pages/intelligence/_config/storeRank/config';

const ENTITY_TYPE = 'mobile';
const GAME_ID_KEY = 'unified_id';
const BUSINESS_TYPE = 'mobile-store-rank-top';
const DOWNLOAD_KEY = 'mobileStoreRankTop';
const DOWNLOAD_EXCEL_LOG_CFG_KEY = 'mobileStoreRankTopDownloadExcel';

const MORE_PAGE_PATH =
  '/intelligence/storeRanks/MobileGlobalRegion/more/alltopcharts';

// 默认筛选器数据，用于reset时、兜底
const DEFAULT_FILTER: IStoreRankTopFilter = {
  entity_type: ENTITY_TYPE,
  store: 'appstore',
  region_type: 'market',
  regions: ['us'],
  genre: [],
  sub_genre: [],
  genre_source: 'sensortower',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
};

type IMobileStoreRankTopProps = IBaseProps & {
  condKey?: string;
  pageLogConfigKey?: string;
};

const MobileStoreRankTop: React.FC<IMobileStoreRankTopProps> = (props) => {
  const { condKey, pageLogConfigKey } = props;
  const { formatMessage } = useIntl();

  // 热门标签选项，url 不传则自动从当前页面获取
  // filterTagName 用于确定 HotTagsSource 的默认选中项
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');

  // 获取品类 options 数据（用于 parseUrlFilterParams 解析品类筛选器值）
  const { loadingStd: definitionsLoading, genreOptionsMap } = useQueryDefinition();
  const genreOptionsMapRef = useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  // 筛选器组件 ref，用于调用 setValueAndConfirm
  const filterRef = useRef<IAllTopChartsFilterInstance | null>(null);

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.storerankMobileTopcharts?.type,
    rangeTimeType: FilterTimeType.sameDay,
  });

  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterName = getFilterName(userInfo);
  const filterData = getFilterValueStorage(userInfo);
  const { filterExtend, setFilterExtend } = useFilterExtend({});
  const filterState = useSelector(
    (rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE],
  );
  // 是否操作过筛选器，判断是否继承的条件
  const isOperate = filterState?.isOperate || false;
  const [initialFilter, setinitialFilter] = useState(DEFAULT_FILTER);
  const {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化
    onPageInit,
    // 筛选器confirm的回调
    onFilterConfirm,
    // 筛选器reset的回调
    onFilterReset,
    // 榜单卡片点击游戏的回调
    onCardGameClick,
    getDetailGamePath,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore,
  } = useStoreRankTopCore({
    entityType: ENTITY_TYPE,
    // 游戏id的取值字段
    gameIdKey: GAME_ID_KEY,
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath: MORE_PAGE_PATH,
    // 页面打点配置的key
    pageLogCfgKey: pageLogConfigKey || 'mobileStoreRankTop',
    // 是否操作过筛选器
    isOperate,
    // 初始化筛选器数据
    initialFilter,
    // 查询榜单数据 初始化的分页大小
    initialPageSize: 10,
  });

  const locale = getLocale();

  const downloadParams = useMemo(() => {
    return {
      entity_type: ENTITY_TYPE,
      store: filter?.store || DEFAULT_FILTER.store,
      region_type: filter?.region_type || DEFAULT_FILTER.region_type,
      regions: filter?.regions || DEFAULT_FILTER.regions,
      genre: filter?.genre || [],
      sub_genre: filter?.sub_genre || [],
      genre_source: filter?.genre_source || DEFAULT_FILTER.genre_source,
      iegg_genre_req: filter?.iegg_genre_req || [],
      granularity: filter?.granularity || DEFAULT_FILTER.granularity,
      start_date: filter?.start_date || '',
      end_date: filter?.end_date || '',
      rankingSources: MOBILE_SOURCES,
    };
  }, [filter]);

  const downloadFileName = useMemo(() => {
    const startDate = filter?.start_date || '';
    const endDate = filter?.end_date || '';
    return `Store Ranks_Mobile_Top_${startDate}_${endDate}`;
  }, [filter?.start_date, filter?.end_date]);

  /**
   * HotTagsSource 选项变化回调
   * 通过 parseUrlFilterParams 解析 hotTag value 为筛选器 UI 格式，
   * 直接回填筛选器表单值，真正请求交给确认按钮
   */
  const handleHotTagConfirm = React.useCallback(async (option: any, isInitialize = false) => {
    if (!option?.value) return;
    try {
      const parsedFilterValue = await parseUrlFilterParams(option.value, { genreOptionsMap: genreOptionsMapRef.current });
      if (parsedFilterValue && Object.keys(parsedFilterValue).length > 0) {
        const nextFilterValue = {
          ...parsedFilterValue,
          [filterKeyMap.HOT_TAGS_SOURCE]: { value: option },
        };
        // 额外处理 region 字段：映射到 StoreRanks 的国家筛选器
        if (option.value?.region) {
          nextFilterValue[filterKeyMap.COMPOSED_COUNTRY_SELECT] = {
            value: {
              type: 'single',
              countries: [option.value.region],
            },
          };
        }
        if (isInitialize) {
          filterRef.current?.setValueAndConfirm(nextFilterValue, { resetToDefault: true });
        } else {
          filterRef.current?.setFormValue(nextFilterValue, { resetToDefault: true });
        }
      }
    } catch (error) {
      console.error('[MobileStoreRankTop] handleHotTagConfirm failed:', error);
    }
  }, []);

  const { regionOptions: regionsOptions } = useMobileStoreRankRegionOptions();

  const handleClickMultiCountry = () => {
    const source = tops?.[0]?.name;
    if (!source) return;
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('source', encodeURIComponent(source));
    urlSearchParams.append('isCheckAllCountries', '1');
    history.push(`${MORE_PAGE_PATH}?${urlSearchParams.toString()}`);
  };

  useEffect(() => {
    if (!userInfo?.userid) {
      // 拿缓存值需要拿到用户id
      return;
    }

    const initFilter = async () => {
      // 有 filter_tag_name 时，跳过初始化请求
      // 改由下方的 useEffect 监听 hotTagsOptions 加载完成后通过 handleHotTagConfirm 触发筛选器 onConfirm
      const tagName = condKey || (history.location.query?.filter_tag_name as string);
      if (tagName) {
        return;
      }

      let newFetchParams = { ...DEFAULT_FILTER };

      // 优先级：1. 用户手动选择的参数（filterExtend） 2. 保存的筛选方案 3. 默认值
      if (filterState.isOperate === true) {
        /** 优先获取用户手动选择的继承参数 */
        const extendParams = getExtendParams();
        if (extendParams && Object.keys(extendParams).length > 0) {
          newFetchParams = { ...newFetchParams, ...extendParams };
        }
      } else {
        // 用户未手动选择，使用保存的筛选方案
        if (filterName) {
          const filterSaveParams = getFilterSaveParams();
          newFetchParams = { ...newFetchParams, ...filterSaveParams };
        }
      }

      newFetchParams.store = (history.location.query?.store as string) || newFetchParams.store;

      if(!newFetchParams.start_date || !newFetchParams.end_date){
        const today = new Date().toISOString().split('T')[0];
        newFetchParams.start_date = today;
        newFetchParams.end_date = today;
      }
      setinitialFilter(newFetchParams);
      onPageInit(newFetchParams);
    };

    initFilter();
  }, [userInfo?.userid, condKey]);

  /**
   * hotTagsOptions 和 genreOptionsMap 都加载完成后，匹配 filter_tag_name 对应的选项，
   * 通过 handleHotTagConfirm 更新筛选器组件（与用户手动选择 HotTag 走同一条路径）
   */
  useEffect(() => {
    const tagName = condKey || (history.location.query?.filter_tag_name as string) || filterTagName;
    if (!tagName || hotTagsLoading || !hotTagsOptions?.length || definitionsLoading) return;
    const matchedOption = hotTagsOptions.find((opt: any) => opt?.name === tagName);
    if (matchedOption) {
      handleHotTagConfirm(matchedOption, true);
    }
  }, [hotTagsLoading, hotTagsOptions, definitionsLoading]);

  const getExtendParams = () => {
    return handleFilterParams(filterExtend);
  };

  const getFilterSaveParams = () => {
    return handleFilterParams(filterData?.apiValue);
  };

  const handleFilterParams = (payload: any) => {
    const params = handleRangeDate2SingleDateRangeTimeFilterExtend(payload);
    const newFetchParams: any = {};
    const timeExtend = params?.[FILTER_RANGE_TIME];
    const singlePlatformSelectFilterExtend =
      params?.[FILTER_SINGLE_PLATFORM_SELECT];
    const composedCountrySelectFilterExtend =
      params?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const genreSelectFilterExtend = params?.[FILTER_GENRE_SELECT];

    if (timeExtend && timeExtend?.filterType === 'date') {
      const isSameDay = isSameDate(
        timeExtend?.start_date,
        timeExtend?.end_date,
        'day',
      );
      if (isSameDay) {
        // newFetchParams.date_type = timeExtend?.granularity || 'monthly';
        // newFetchParams.date = timeExtend?.start_date || '';
        newFetchParams.start_date = timeExtend?.start_date || '';
        newFetchParams.end_date = timeExtend?.start_date || '';
      }
    }

    if (singlePlatformSelectFilterExtend) {
      const initialStore =
        singlePlatformSelectFilterExtend?.value || DEFAULT_FILTER?.store;
      newFetchParams.store = initialStore;
    }
    if (composedCountrySelectFilterExtend) {
      const initialRegions =
        composedCountrySelectFilterExtend?.countries?.length === 1
          ? composedCountrySelectFilterExtend?.countries
          : DEFAULT_FILTER?.regions;
      newFetchParams.regions = initialRegions;
    }
    if (genreSelectFilterExtend) {
      Object.assign(
        newFetchParams,
        handleGenreFilterParams(genreSelectFilterExtend),
      );
    }

    // 处理 Product Model 继承
    const productModelFilterExtend = params?.[SENSOR_TOWER_TAG_PRODUCT_MODEL];
    if (productModelFilterExtend?.value?.length > 0) {
      newFetchParams[SENSOR_TOWER_TAG_PRODUCT_MODEL] = productModelFilterExtend;
    }

    return {
      ...DEFAULT_FILTER,
      ...newFetchParams,
    };
  };

  const multiCountryText = (
    <span className="dbi-storerank-single-country-select-footer">
      {formatMessage({ id: 'dbi_store_rank_single_country_select_footer_btn_text' })}
      <SvgIcon type="aigc-jump" />
    </span>
  );

  return (
    <StoreRankTopCore
      className="dbi-pc-store-rank-top dbi-alltopcharts"
      entityType={ENTITY_TYPE}
      showDownloadTips
      // 游戏id的取值字段
      gameIdKey={GAME_ID_KEY}
      isLoading={isLoading}
      tops={tops}
      filter={
        <MobileStoreRankTopFilter
          ref={filterRef}
          style={{ marginBottom: !isLoading && tops?.length > 0 ? 10 : 20 }}
          isOperate={isOperate}
          regionOptions={regionsOptions}
          multiCountryText={multiCountryText}
          onClickMultiCountry={handleClickMultiCountry}
          filter={filter}
          onConfirm={onFilterConfirm}
          onReset={onFilterReset}
          rangeTimeComponentDefault={latestTimeTypeHasData}
          entityType={ENTITY_TYPE}
          hotTagsOptions={hotTagsOptions}
          filterTagName={condKey || (history.location.query?.filter_tag_name as string) || filterTagName}
          onHotTagConfirm={handleHotTagConfirm}
        />
      }
      exportTable={
        <ExportTable
          entityType={ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={DOWNLOAD_EXCEL_LOG_CFG_KEY}
          isDisableDownload={isLoading}
          total={tops?.length ?? 0}
          tableParams={downloadParams}
          newStyleFileName={downloadFileName}
          isShowMaxDownloadRowsTips={false}
          downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
          useDownloadAmountType={true}
          useWebDownload={true}
          downloadKey={DOWNLOAD_KEY}
          regionOptions={regionsOptions}
        />
      }
      onCardTitleClick={onGoToStoreRankMore}
      onCardGameClick={onCardGameClick}
      getDetailGamePath={getDetailGamePath}
    />
  );
};

export default MobileStoreRankTop;
